import { TFunction } from 'i18next';

import { useAppSelector } from '@/app/hook';
import { ChatbotRTKService } from '@/app/services/chatbot-rtk.services';
import { selectClient } from '@/features/dashboard.slice';

import { INote } from '../components/dashboard/knowledge-bases-create/NoteForm';
import { createRandomString, isAlphabetic } from '../components/tools';

const removeEmptyStrings = (obj: object): object => {
  // Handle different object types: null, undefined, arrays, and plain objects
  if (obj === null || obj === undefined) {
    return obj;
  } else if (Array.isArray(obj)) {
    return obj.filter(item => removeEmptyStrings(item)); // Recursively filter array elements
  } else if (typeof obj === 'object') {
    // Iterate over object keys and create a new object
    return Object.keys(obj).reduce((acc, key) => {
      const value = removeEmptyStrings(obj[key]); // Recursively remove empty strings
      // Add key-value pair to accumulator only if value is not an empty string
      if ((value !== undefined && typeof value !== 'string') || value.trim() !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
  }
  // Return non-object values and empty arrays/objects as-is
  return obj;
};

function checkImageMimeType(file: any) {
  const imageMimeTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];

  if (file && imageMimeTypes.includes(file.type)) {
    return true;
  }
  return false;
}

function checkVideoMimeType(file: any) {
  const mimeType = file.type;
  const keywords = ['video'];

  // Remove the quotes from the string if necessary
  const cleanedMimeType = mimeType.replace(/['"]+/g, '');

  // Check if any keyword in the list is present in the mimeType
  const containsKeyword = keywords.some(keyword => cleanedMimeType.includes(keyword));

  return containsKeyword;
}

function checkFileSize(size: number, file: any) {
  const maxSize = size * 1024 * 1024; // 30 MB in bytes
  if (file && file.size > maxSize) {
    return false;
  } else {
    return true;
  }
}

enum Video {
  MP4 = 'video/mp4',
  MPEG = 'video/mpeg',
  WEBM = 'video/webm',
  OGG = 'video/ogg',
  MOV = 'video/mov',
  X_SGI = 'video/x-sgi-movie',
  QUICK_TIME = 'video/quicktime',
  M4V = 'video/m4v',
  X_M4V = 'video/x-m4v',
  X_MS = 'video/x-msvideo',
  X_F4V = 'video/x-f4v',
  X_MS_WMV = 'video/x-ms-wmv',
  MKV = 'video/mkv',
  X_MATROSKA = 'video/x-matroska',
  AVI = 'video/avi',
  AVI_3GPP2 = 'video/3ggp2',
  AVI_3GPP = 'video/3ggp',
  X_MS_ASF = 'video/x-ms-asf',
  GP_3GP = 'video/3gp',
  GP_3GPP = 'video/3gpp',
  video = 'video',
  Other = 'application/octet-stream',
}

enum Image {
  JPEG = 'image/jpeg',
  JPG = 'image/jpg',
  PNG = 'image/png',
  GIF = 'image/gif',
}

enum Icon {
  JPEG = 'image/jpeg',
  JPG = 'image/jpg',
  PNG = 'image/png',
  GIF = 'image/gif',

  MP4 = 'video/mp4',
  WEBM = 'video/webm',
  OGG = 'video/ogg',
  MOV = 'video/mov',
}

enum File {
  DOCUMENT = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  JSON = 'application/json',
  PDF = 'application/pdf',
  PRESENTATION = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  TEXT = 'text/plain',
  HTML = 'text/html',
}

const MimeType = {
  Video,
  Image,
  Icon,
  File,
};

interface ValidationSuccess {
  is_valid: true;
  value: Record<string, any>;
}

interface ValidationError {
  is_valid: false;
  value: null;
}

type ValidationResult = ValidationSuccess | ValidationError;

function isSupportedVideoFormatMimeType(mimeType: string): boolean {
  return Object.values(Video).includes(mimeType as Video);
}

function isSupportedVideoFormatWithFileName(fileName: string): boolean {
  const extension = fileName.split('.').pop(); // Get file extension
  const enumValues = Object.values(Video); // Get all enum values
  return enumValues.some(value => value.includes(extension as Video));
}

const createBlockId = (text: string, tree: any[]) => {
  let titleCode = 0;
  tree
    .filter(t => t.parent)
    .forEach(element => {
      if (element.block_id) {
        const _code = element.block_id.split('-');
        if (_code[0] && Number(_code[0]) > titleCode) titleCode = +_code[0];
      }
    });

  if (typeof text !== 'string' || !text) {
    return '';
  }
  let acronym = text
    ?.match(/[\p{Alpha}\p{Nd}]+/gu)
    ?.reduce((previous, next) => previous + (+next === 0 || parseInt(next) ? parseInt(next) : next[0] || ''), '')
    ?.toUpperCase();

  if (!acronym || (acronym && acronym.length < 3) || !isAlphabetic(acronym)) {
    acronym = createRandomString(5);
  }

  return `${titleCode + 1}-${acronym?.slice(0, 3)}`;
};

const PartnetBannerLinks = {
  toolkitBox: 'https://cdn.1cdn.app/application/LOUISEBOT/25012813160357_Box.svg',
  alexPlaceholder: 'https://cdn.1cdn.app/application/LOUISEBOT/25012813263175_louise_BP_Alex.png',
  alexLink: 'https://louisebot.com/en-US/chat/d7609aa8-6ff5-4897-908e-a4ddb574d1c4',
};

/**
 * checkAndReturnValidJsonWithVariables
 *
 * This method validates the input JSON string and processes any variables in the format $:var_*!.
 * It ensures proper JSON structure and adds quotes around unquoted variables.
 *
 * @param {string} inputString - The JSON string to validate and process
 * @returns {Promise<ValidationResult>} An object containing:
 *   - is_valid: boolean - Indicates if the JSON is valid
 *   - value: Record<string, any> | null - The parsed JSON object if valid, null otherwise
 *
 * @example
 * const input = `{
 *   "name": "Hello $:var_name!",
 *   "age": $:var_age!
 * }`;
 * const result = await checkAndReturnValidJsonWithVariables(input);
 * if (result.is_valid) {
 *   console.log(result.value);
 * }
 *
 * @throws {never} This method doesn't throw exceptions, errors are handled in the return value
 * @author Milad A
 * @since 2025-01-28
 */
function checkAndReturnValidJsonWithVariables(inputString: string): ValidationResult {
  if (typeof inputString !== 'string') {
    return {
      is_valid: false,
      value: null,
    };
  }

  try {
    const jsonString = inputString.replace(/:\s*(\$:var_[a-zA-Z0-9_]+!)(?=\s*[,}])/g, ': "$1"');

    const jsonObject = JSON.parse(jsonString);

    if (typeof jsonObject !== 'object' || jsonObject === null) {
      return {
        is_valid: false,
        value: null,
      };
    }

    return {
      is_valid: true,
      value: jsonObject,
    };
  } catch (error) {
    return {
      is_valid: false,
      value: null,
    };
  }
}

const setBlockNotes = (block: any) => {
  const { notes } = block;
  const newNotes = notes?.filter((x: INote) => x.title != '');
  if (newNotes?.length > 0) block.notes = newNotes;
  else delete block.notes;
  return block;
};

const renderHiddenToken = (token: string) => {
  if (token?.length > 2) {
    const firstChar = token[0];
    const lastChar = token[token.length - 1];
    const middleStars = '*'.repeat(token.length - 2);
    return firstChar + middleStars + lastChar;
  } else {
    return '';
  }
};

const getBotNameByBotCode = (botCode: string, t: TFunction<'translation', undefined>) => {
  const types: { [key: string]: string } = {
    MCBOT_01: t('DashboardCreatePartial/key3'),
    KITTY_02: t('DashboardCreatePartial/key4'),
    KITTY_03: t('DashboardCreatePartial/key2'),
    CLONE_01: t('DashboardCreatePartial/key1'),
    KITTY_01: t('DashboardCreatePartial/key9'),
    VIDEO_SCENARIO: t('DashboardCreatePartial/key5'),
    KITTY_04: t('DashboardCreatePartial/key6'),
    CALLBOT: t('DashboardCreatePartial/key10'),
  };

  return types[botCode] || botCode;
};

const renderStatusBadge = (status: string) => {
  switch (status) {
    case 'SUBMITTED':
      return 'tw-bg-blue-100 tw-text-blue-800';
    case 'NEED_REVIEW':
      return 'tw-bg-orange-100 tw-text-orange-800';
    case 'PUBLISHED':
      return 'tw-bg-green-100 tw-text-green-800';
    case 'REJECTED':
      return 'tw-bg-red-100 tw-text-red-800';
    case 'OLD_VERSION':
      return 'tw-bg-gray-100 tw-text-gray-800';
    default:
      return '';
  }
};

const useCanCreateMoreChatbots = () => {
  const client = useAppSelector(selectClient);
  const currentHumanClonebots = ChatbotRTKService.endpoints.getAllScenarios.useQuery({ botType: 'link' }).data?.filter(x => x.isFree !== true)?.length || 0;
  const currentChatbots = ChatbotRTKService.endpoints.getAllScenarios.useQuery({ botType: 'widget' }).data?.filter(x => x.isFree !== !true).length || 0;
  const canCreateMoreChatbots = client && (client?.botCreationLimit === -1 || client?.botCreationLimit > currentChatbots + currentHumanClonebots);

  return canCreateMoreChatbots;
};

const renderVersionStatusTitle = (status: string, t: TFunction<'translation', undefined>) => {
  const key = `marketplace/versionStatus/${status}`;
  const translation = t(key);
  return translation !== key ? translation : '';
};

const renderTransactionStatusTitle = (status: string, t: TFunction<'translation', undefined>) => {
  const key = `marketplace/transactionStatus/${status}`;
  const translation = t(key);
  return translation !== key ? translation : status;
};

const makeAllCssImportant = (css: string): string => {
  // Regex: find all property:value pairs ending with ; or }
  return css.replace(/([^:]+:\s*[^;}{]+)(;|})/g, (_, rule, ending) => {
    // If already has !important, leave it
    if (/!important\s*$/.test(rule)) return rule + ending;
    return `${rule.trim()} !important${ending}`;
  });
};

const Utilities = {
  removeEmptyStrings,
  MimeType,
  checkImageMimeType,
  checkFileSize,
  isSupportedVideoFormatWithFileName,
  isSupportedVideoFormatMimeType,
  createBlockId,
  PartnetBannerLinks,
  checkAndReturnValidJsonWithVariables,
  setBlockNotes,
  renderHiddenToken,
  getBotNameByBotCode,
  renderStatusBadge,
  useCanCreateMoreChatbots,
  renderVersionStatusTitle,
  renderTransactionStatusTitle,
  makeAllCssImportant,
};

export default Utilities;
